<template>
  <Modal
    :title="title"
    class-name="refund-modal"
    submit-btn-text="Submit Refund"
    :saving="saving"
    @hide-modal="$emit('hide-modal')"
    @submit="handleSubmit"
  >
    <template #header>
      <div class="my-1">
        <span>
          <b>{{ invoice.customer.full_name }}</b>
        </span>
        <AddressLabel
          :address="invoice.service_address || invoice.billing_address"
          single
        />
      </div>
    </template>
    <div>
      <ButtonToggle
        v-if="cardTransactions.length"
        v-model="refundType"
        :options="options"
        class="toggle"
      />
      <div v-else class="static-refund-header">Cash/Check Refund</div>
      <Transition>
        <div v-if="refundType === 'card'">
          <table class="full-width transactions-table mt-3">
            <thead>
              <tr>
                <th class="radio-button-col"></th>
                <th class="date-col">Date</th>
                <th class="text-right">Refundable Amount</th>
                <th>Payment Method</th>
                <th>Cardholder Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="transaction in cardTransactions" :key="transaction.id">
                <td>
                  <RadioButton
                    :key="transaction.id"
                    v-model="selectedTransactionId"
                    class="radio-button"
                    :radio-value="transaction.id"
                  />
                </td>
                <td>{{ shortDate(transaction.transaction_time) }}</td>
                <td class="text-right">
                  {{ dollars(transaction.refundable_amount_cents) }}
                </td>
                <td>
                  <component
                    :is="cardImages[transaction.card_brand]"
                    class="mr-2"
                  />
                  {{ methodLabel(transaction) }}
                </td>
                <td>{{ transaction.payer_name }}</td>
              </tr>
            </tbody>
          </table>
          <hr class="mb-0" />
        </div>
      </Transition>
      <div class="d-flex mt-3">
        <b>{{ refundLabel }}</b>
        <span class="text-gray font-italic ml-1"
          >(Please note all refunds are final)</span
        >
      </div>
      <div
        class="d-flex justify-content-between align-items-center payment-values my-2 p-2"
      >
        <div class="d-flex">
          <span class="mr-2 nowrap">Payments Total:</span>
          <span>{{
            dollars(invoice.financials.total_transactions_cents)
          }}</span>
        </div>
        <div class="d-flex">
          <span class="mr-2 nowrap">Refundable Amount:</span>
          <span
            :class="[
              'mr-1',
              {
                'text-danger': invoice.financials.refundable_amount_cents > 0
              }
            ]"
            >{{ dollars(refundableAmount) }}</span
          >
        </div>
        <div class="d-flex justify-content-end payment-amount-label">
          <span class="mr-2 mt-2 nowrap">Refund Amount:</span>
          <TextField
            v-model="refundAmount"
            name="refundAmount"
            currency
            style="width: 130px"
            class="payment-amount-field"
            input-class="text-right"
            label="Payment Amount"
            no-label
          />
        </div>
        <div
          v-if="v$.refundAmount.$errors.length"
          class="refund-payment-message d-flex"
        >
          <div class="d-flex align-items-center ml-auto">
            <span class="icon fa-regular fa-triangle-exclamation mr-2"></span>
            <span>{{ v$.refundAmount.$errors[0]?.$message }}</span>
          </div>
        </div>
      </div>
      <TextArea
        v-model="notes"
        label="Refund Note"
        hint-label="(Add details about this refund)"
        class="full-width mt-2"
      />
    </div>
  </Modal>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { mapActions } from "vuex";

import AddressLabel from "@/components/AddressLabel.vue";
import ButtonToggle from "@/components/ButtonToggle.vue";
import Modal from "@/components/Modal.vue";
import RadioButton from "@/components/RadioButton.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import Transition from "@/components/Transition.vue";
import amexSrc from "@/images/amex.svg?component";
import mastercardSrc from "@/images/mastercard.svg?component";
import visaSrc from "@/images/visa.svg?component";
import { Invoice } from "@/typings/invoice";
import { Transaction, TransactionType } from "@/typings/transaction";
import useVuelidate from "@vuelidate/core";
import { helpers, maxValue, minValue, required } from "@vuelidate/validators";

export default {
  components: {
    Modal,
    AddressLabel,
    RadioButton,
    Transition,
    TextField,
    TextArea,
    ButtonToggle
  },
  props: {
    invoice: {
      type: Object as () => Invoice,
      default: null
    }
  },
  emits: ["hide-modal"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      refundType: TransactionType.CARD,
      selectedTransactionId: null,
      refundAmount: 0,
      notes: "",
      saving: false,
      options: [
        {
          value: TransactionType.CARD,
          label: "Credit Card Refund"
        },
        {
          value: TransactionType.CHECK,
          label: "Cash/Check Refund"
        }
      ],
      cardImages: {
        Visa: visaSrc,
        MasterCard: mastercardSrc,
        "American Express": amexSrc
      }
    };
  },
  computed: {
    cardTransactions() {
      return this.invoice.payments.filter(
        (transaction: Transaction) =>
          transaction.transaction_type === TransactionType.CARD &&
          transaction.links?.payment_refund
      );
    },
    refundableAmount() {
      if (!this.selectedTransaction) {
        return this.invoice.financials.refundable_amount_cents;
      }
      return this.selectedTransaction.refundable_amount_cents;
    },
    refundLabel() {
      if (!this.selectedTransaction) {
        return `Refund ${this.dollars(
          this.invoice.financials.refundable_amount_cents
        )} or less`;
      }
      const { refundable_amount_cents, card_brand, card_last4 } =
        this.selectedTransaction;
      return `Refund ${this.dollars(
        refundable_amount_cents
      )} or less using ${card_brand} ****${card_last4}`;
    },
    selectedTransaction(): Transaction {
      return this.invoice.payments.find(
        (transaction: Transaction) =>
          transaction.id === this.selectedTransactionId
      );
    },
    title() {
      return `Issue Refund for Invoice #${this.invoice.invoice_id}`;
    },
    isSameDayTransaction() {
      if (!this.selectedTransaction) {
        const recentPayment =
          this.invoice.payments[this.invoice.payments.length - 1];
        return recentPayment?.transaction_time
          ? moment(new Date()).diff(recentPayment?.transaction_time, "hours") <
              24
          : false;
      }
      return (
        moment(new Date()).diff(
          this.selectedTransaction.transaction_time,
          "hours"
        ) < 24
      );
    },
    sameDayPartialRefund() {
      const partialRefund = this.refundAmount < this.refundableAmount;
      return partialRefund && this.isSameDayTransaction;
    }
  },
  watch: {
    refundType(newValue: TransactionType, oldValue: TransactionType) {
      if (newValue !== oldValue) {
        if (newValue === TransactionType.CARD) {
          this.selectDefaultTransaction();
        } else {
          this.selectedTransactionId = null;
        }
      }
    },
    refundAmount(newValue: number) {
      if (newValue < 0) {
        this.refundAmount = Math.abs(newValue);
      }
    }
  },
  created() {
    if (!this.cardTransactions.length) {
      this.refundType = TransactionType.CHECK;
    }
    this.selectDefaultTransaction();
  },
  validations() {
    return {
      refundAmount: {
        required,
        minValue: minValue(0),
        maxValue: helpers.withMessage(
          "Cannot refund more than the refundable amount",
          maxValue(this.invoice.financials.total_transactions_cents)
        ),
        $autoDirty: true
      }
    };
  },
  methods: {
    ...mapActions(["createRefund"]),
    handleSubmit() {
      if (
        this.refundType === TransactionType.CARD &&
        this.sameDayPartialRefund
      ) {
        window.notify.error(
          "Partial refunds can only be processed after 1 business day. Full refunds can still be issued on the same day the transaction was processed."
        );
        return;
      }
      this.v$.$validate().then((valid: boolean) => {
        if (valid) {
          this.saving = true;
          // Use the url on the transaction
          // or get the link from the invoice?
          const url = this.selectedTransaction
            ? this.selectedTransaction.links?.payment_refund
            : this.invoice.links?.invoice_refund;

          this.createRefund({
            url,
            data: {
              amount_cents: this.refundAmount,
              notes: this.notes
            }
          })
            .then(() => {
              window.notify.success("Refund success");
              this.$emit("hide-modal");
            })
            .finally(() => {
              this.saving = false;
            });
        }
      });
    },
    methodLabel(transaction: Transaction) {
      return `**** ${transaction.card_last4}`;
    },
    selectDefaultTransaction() {
      if (this.cardTransactions.length) {
        this.selectedTransactionId = this.cardTransactions[0].id;
      }
    }
  }
};
</script>

<style lang="scss">
.refund-payment-message {
  margin-left: auto;
  color: var(--danger);
  font-size: 14px;
  .icon {
    font-size: 14px;
  }
}
.refund-modal {
  .toggle {
    width: 270px;
  }
  .transactions-table {
    th,
    td {
      padding: 0 20px;
      &:first-of-type {
        padding: 0;
      }
      &:last-of-type {
        width: 100%;
      }
    }
  }
  .radio-button {
    font-size: 1rem;
  }
  .radio-button-col {
    width: 40px;
  }
  .date-col {
    width: 120px;
  }
  .payment-values {
    font-size: 0.9rem;
    flex-wrap: wrap;
    background-color: #dee2e6;
    border-radius: 3px;
  }
  .payment-amount-label {
    margin-top: 7px;
  }
  .payment-amount-field {
    width: 75px !important;
  }
  .nowrap {
    white-space: nowrap;
  }
  .static-refund-header {
    color: var(--darkblue);
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: -7px;
  }
}
</style>
